import { PlusIcon, XIcon } from 'lucide-react';
import type { GanttActivity } from '../../types/portal';
import { fitMonths } from '../../data/fieldMap';

interface GanttTableProps {
  activities: GanttActivity[];
  /** Driven by the study duration, so the grid matches what was proposed. */
  monthCount: number;
  onChange: (activities: GanttActivity[]) => void;
}

export function GanttTable({ activities, monthCount, onChange }: GanttTableProps) {
  const months = Array.from({ length: monthCount }, (_, i) => i + 1);

  const addRow = () => {
    onChange([
      ...activities,
      { id: `g${Date.now()}`, activity: '', months: months.map(() => false) }
    ]);
  };

  const updateActivity = (id: string, activity: string) => {
    onChange(activities.map((row) => (row.id === id ? { ...row, activity } : row)));
  };

  const toggleMonth = (id: string, monthIndex: number) => {
    onChange(
      activities.map((row) =>
        row.id === id
          ? {
              ...row,
              months: fitMonths(row.months, monthCount).map((on, i) =>
                i === monthIndex ? !on : on
              )
            }
          : row
      )
    );
  };

  const removeRow = (id: string) => {
    onChange(activities.filter((row) => row.id !== id));
  };

  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <div className="section-head">
        <p className="field-hint">
          Mark the months during which each activity will be carried out. The grid follows
          the study duration you entered.
        </p>
        <button type="button" className="btn btn--secondary btn--small" onClick={addRow}>
          <PlusIcon size={15} aria-hidden="true" />
          Add New Gantt Activity
        </button>
      </div>

      <div className="table-scroll">
        <table className="data-table">
          <caption className="sr-only">Month-wise time frame of activities</caption>
          <thead>
            <tr>
              <th scope="col" className="col-sr">
                Sr
              </th>
              <th scope="col" style={{ minWidth: 220 }}>
                Activity
              </th>
              {months.map((m) => (
                <th scope="col" key={m} className="cell-center">
                  Month {m}
                </th>
              ))}
              <th scope="col" className="col-action">
                Delete
              </th>
            </tr>
          </thead>
          <tbody>
            {activities.length === 0 ? (
              <tr>
                <td colSpan={monthCount + 3}>
                  <p className="table-empty">
                    No activities yet — add your first Gantt activity.
                  </p>
                </td>
              </tr>
            ) : (
              activities.map((row, index) => {
                const cells = fitMonths(row.months, monthCount);
                return (
                  <tr key={row.id}>
                    <td className="col-sr">{index + 1}</td>
                    <td>
                      <label className="sr-only" htmlFor={`gantt-${row.id}`}>
                        Activity {index + 1} name
                      </label>
                      <input
                        id={`gantt-${row.id}`}
                        className="input"
                        value={row.activity}
                        placeholder="e.g. Data collection"
                        onChange={(e) => updateActivity(row.id, e.target.value)}
                      />
                    </td>
                    {cells.map((on, monthIndex) => (
                      <td key={monthIndex} className="cell-center">
                        <button
                          type="button"
                          className={`gantt-cell${on ? ' is-on' : ''}`}
                          aria-pressed={on}
                          aria-label={`Month ${monthIndex + 1} for activity ${index + 1}`}
                          onClick={() => toggleMonth(row.id, monthIndex)}
                        >
                          X
                        </button>
                      </td>
                    ))}
                    <td className="col-action">
                      <button
                        type="button"
                        className="btn-icon btn-icon--danger"
                        aria-label={`Delete activity ${index + 1}`}
                        onClick={() => removeRow(row.id)}
                      >
                        <XIcon size={15} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
